<script setup lang="ts">
  import { computed } from 'vue'
  import { formatTopBarEndsOn } from '@/helpers/top-bars'
  import type { TTopBarVariant } from '@/types/top-bars'
  import DesButtonLink from '@/components/button/DesButtonLink.vue'
  import DesButtonIconLite from '@/components/button/DesButtonIconLite.vue'

  const props = defineProps<{
    variant: TTopBarVariant
    message: string
    badge?: string | null
    endsOn?: string | null
    linkLabel?: string | null
    linkUrl?: string | null
  }>()

  defineEmits<{
    close: []
    action: []
  }>()

  const barClasses: Record<TTopBarVariant, string> = {
    feature: 'bg-purple-90 border-purple-70 text-black-10',
    promo: 'bg-blue-90 border-blue-70 text-black-10',
    alert: 'bg-red-90 border-red-70 text-black-10',
  }

  const badgeClasses: Record<TTopBarVariant, string> = {
    feature: 'bg-purple-50 text-white',
    promo: 'bg-blue-50 text-white',
    alert: 'bg-red-50 text-white',
  }

  const linkClasses: Record<TTopBarVariant, string> = {
    feature: '!text-purple-50 !font-bold',
    promo: '!text-blue-50 !font-bold',
    alert: '!text-red-50 !font-bold',
  }

  const endsText = computed<string>(() =>
    props.variant === 'feature' ? '' : formatTopBarEndsOn(props.endsOn ?? null),
  )
  const isExternalLink = computed<boolean>(() => !!props.linkUrl && /^https?:\/\//.test(props.linkUrl))

  const linkAttrs = computed(() =>
    isExternalLink.value ? { href: props.linkUrl ?? undefined, target: '_blank' } : { to: props.linkUrl ?? undefined },
  )
</script>

<template>
  <div
    class="relative flex items-center justify-center gap-3 min-h-11 rounded-xl border px-12 py-2 text-sm"
    :class="barClasses[variant]"
  >
    <span
      v-if="badge"
      class="rounded-full px-2.5 py-px text-2xs font-extrabold uppercase tracking-wider whitespace-nowrap"
      :class="badgeClasses[variant]"
    >
      {{ badge }}
    </span>

    <span class="font-bold">
      {{ message }}
      <span v-if="endsText" class="text-black-40 font-normal">· Ends {{ endsText }}</span>
    </span>

    <DesButtonLink
      v-if="linkLabel && linkUrl"
      v-bind="linkAttrs"
      :class="linkClasses[variant]"
      @click="$emit('action')"
    >
      {{ linkLabel }}
    </DesButtonLink>

    <DesButtonIconLite icon="fa-light fa-times" class="absolute right-2" @click="$emit('close')" />
  </div>
</template>
